import { useCallback, useMemo, useRef, useState } from "react"
import { type FormikProps } from "formik"
import { toast } from "react-toastify"

import { LoadingView, StepProgress, SteppedForm, type Step } from "commons"
import { usePractitioners } from "practitioner"
import { useOrganizationInfo } from "organization"
import { PracticeSettings } from "settings"

import { formValuesToPutPersonRequest, getInitialOnboardingState, personToFormValues } from "patient/utils"
import { step1Schema, step2Schema, step3Schema, step4Schema, TOTAL_ONBOARDING_STEPS } from "patient/validation"
import { useOnboardingInfo } from "patient/hooks/useOnboardingInfo"
import { usePutPerson } from "patient/hooks/usePutPerson"
import type { OnboardingPersonFormType } from "patient/types"

import { OnboardingStepContact } from "../steps/OnboardingStepContact"
import { OnboardingStepDemographics } from "../steps/OnboardingStepDemographics"
import { OnboardingStepName } from "../steps/OnboardingStepName"
import { OnboardingStepPractitioner } from "../steps/OnboardingStepPractitioner"
import { Onboarded } from "./Onboarded"
import { PatientOnboardingLayout } from "./PatientOnboardingLayout"
import { PatientPortalHeader } from "./PatientPortalHeader"
import { TermsAcceptedField } from "./TermsAcceptedField"
import {
  OnboardingRecaptcha,
  RecaptchaAction,
  RecaptchaError,
  type OnboardingRecaptchaHandle,
} from "./OnboardingRecaptcha"

const ONBOARDING_ACTION: Record<string, RecaptchaAction> = {
  STEP: "onboarding_step",
  SUBMIT: "onboarding_submit",
}

const OnboardPatient: React.FC<IProps> = ({ settings, practiceIdentifier, personIdentifier }) => {
  const { practitioners } = usePractitioners(practiceIdentifier)
  const { orgName } = useOrganizationInfo(practiceIdentifier)
  const { data: onboardingInfo, isLoading: isLoadingInfo } = useOnboardingInfo(practiceIdentifier, personIdentifier)
  const { mutateAsync: putPersonAsync, isPending, variables } = usePutPerson()
  const isFinalSubmitPending = isPending && variables?.transient === false
  const recaptcha = useRef<OnboardingRecaptchaHandle>(null)
  const stepToken = useRef<string>()

  const [step, setStep] = useState(1)
  const [success, setSuccess] = useState<SuccessState>()

  const defaultPractitioner = practitioners.length === 1 ? (practitioners[0]?.value ?? "") : ""

  const organizationName = onboardingInfo?.practiceInformation?.name ?? orgName

  const initialValues = useMemo(() => {
    const base = getInitialOnboardingState(practiceIdentifier, defaultPractitioner)
    if (onboardingInfo?.personInformation) {
      return personToFormValues(onboardingInfo.personInformation, base)
    }
    return base
  }, [practiceIdentifier, defaultPractitioner, onboardingInfo])

  const steps = useMemo<Step<OnboardingPersonFormType>[]>(
    () => [
      {
        validationSchema: step1Schema,
        component: () => <OnboardingStepName organizationName={organizationName} />,
      },
      {
        validationSchema: step2Schema,
        component: () => (
          <OnboardingStepContact organizationId={practiceIdentifier} organizationName={organizationName} />
        ),
      },
      {
        validationSchema: step3Schema,
        component: () => <OnboardingStepDemographics />,
      },
      {
        validationSchema: step4Schema,
        component: () => <OnboardingStepPractitioner practitioners={practitioners} />,
        renderFooterStart: () => <TermsAcceptedField />,
        footerActions: [
          {
            label: "Back",
            type: "button",
            className:
              "p-button-sm p-button-outlined !border-gray-300 !text-gray-500 hover:!bg-gray-50 !font-semibold focus:!ring-0 focus:!ring-offset-0 focus:!outline-none !shadow-none focus:!bg-gray-50 focus:!text-gray-500",
            disabled: () => isFinalSubmitPending,
            onClick: ({ onBack }) => onBack(),
          },
          {
            label: "Complete & Continue",
            type: "submit",
            className: "button-primary p-button-sm",
            loading: ({ isSubmitting }) => isSubmitting || isFinalSubmitPending,
            disabled: ({ isSubmitting }) => isSubmitting || isFinalSubmitPending,
          },
        ],
      },
    ],
    [organizationName, practiceIdentifier, practitioners, isFinalSubmitPending],
  )

  const beforeStepAdvance = useCallback(
    async (_values: OnboardingPersonFormType, stepIndex: number, formik: FormikProps<OnboardingPersonFormType>) => {
      if (stepIndex === 1) {
        const emailFieldError = await formik.validateField("emails[0].value")
        if (emailFieldError) return false
      }
      try {
        if (!recaptcha.current) throw new RecaptchaError("Human verification is not ready. Please try again.")
        stepToken.current = await recaptcha.current.getToken(ONBOARDING_ACTION.STEP)
        return true
      } catch (error) {
        toast.error(error instanceof RecaptchaError ? error.message : "Human verification failed. Please try again.")
        return false
      }
    },
    [],
  )

  const handleStepAdvance = useCallback(
    async (
      values: OnboardingPersonFormType,
      _stepIndex: number,
      formik: FormikProps<OnboardingPersonFormType>,
      { rollback }: { rollback: () => void },
    ) => {
      try {
        const info = await putPersonAsync({
          ...formValuesToPutPersonRequest(values, true),
          recaptchaToken: stepToken.current,
        })
        formik.setValues((current) => personToFormValues(info.personInformation, current))
      } catch {
        rollback()
        toast.error("Unable to save your progress. Please verify again and retry.")
      } finally {
        stepToken.current = undefined
      }
    },
    [putPersonAsync],
  )

  const handleFinalSubmit = useCallback(
    async (values: OnboardingPersonFormType, formik: FormikProps<OnboardingPersonFormType>) => {
      try {
        if (!recaptcha.current) throw new RecaptchaError("Human verification is not ready. Please try again.")
        const recaptchaToken = await recaptcha.current.getToken(ONBOARDING_ACTION.SUBMIT)
        await putPersonAsync({ ...formValuesToPutPersonRequest(values, false), recaptchaToken })
        setSuccess({
          name: `${values.firstName} ${values.lastName}`.trim(),
          email: values.emails[0]?.value ?? "",
        })
      } catch (error) {
        toast.error(
          error instanceof RecaptchaError
            ? error.message
            : "Unable to complete enrollment. Please verify again and retry.",
        )
      } finally {
        formik.setSubmitting(false)
      }
    },
    [putPersonAsync],
  )

  if (isLoadingInfo && personIdentifier) {
    return <LoadingView />
  }

  if (success) {
    return (
      <PatientOnboardingLayout settings={settings}>
        <PatientPortalHeader
          logoUrl={settings.logo}
          patientPortalUrl={window.VITE_APP_PATIENT_PORTAL_URL}
          shouldShowPatientPortalLink={false}
          headerText="Enrollment successful"
        />
        <Onboarded name={success.name} email={success.email} />
      </PatientOnboardingLayout>
    )
  }

  return (
    <PatientOnboardingLayout settings={settings}>
      <PatientPortalHeader logoUrl={settings.logo} patientPortalUrl={window.VITE_APP_PATIENT_PORTAL_URL} />
      <StepProgress currentStep={step} totalSteps={TOTAL_ONBOARDING_STEPS} />
      <div className="bg-white rounded-2xl shadow-[0px_0px_14px_#18300024] w-full">
        <SteppedForm
          steps={steps}
          initialValues={initialValues}
          beforeStepAdvance={beforeStepAdvance}
          onStepAdvance={handleStepAdvance}
          onFinalSubmit={handleFinalSubmit}
          onStepChange={setStep}
          beforeFooter={<OnboardingRecaptcha ref={recaptcha} />}
          isBusy={isPending}
        />
      </div>
    </PatientOnboardingLayout>
  )
}

type SuccessState = {
  name: string
  email: string
}

type IProps = {
  settings: PracticeSettings
  practiceIdentifier: string
  personIdentifier?: string
}

export { OnboardPatient }
